import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ChevronRight, CircleDot, Clock, RefreshCw } from 'lucide-react';
import {
  AREAS_ATENCION,
  type AreaAtencion,
  type AsuntoAtencion,
  type Atencion,
  type GravedadAtencion,
} from '@/lib/atencion';
import { classNames } from '@/lib/utils';

/**
 * Cada gravedad lleva su icono y su etiqueta además del color: el triángulo,
 * el reloj y el punto se distinguen sin ver colores, y la etiqueta la leen el
 * lector de pantalla y el `title`.
 */
const GRAVEDAD: Record<GravedadAtencion, {
  etiqueta: string;
  Icono: typeof AlertTriangle;
  caja: string;
  icono: string;
  numero: string;
}> = {
  urgente: { etiqueta: 'Urgente', Icono: AlertTriangle, caja: 'bg-red-50', icono: 'text-red-700', numero: 'text-red-700' },
  semana: { etiqueta: 'Esta semana', Icono: Clock, caja: 'bg-amber-50', icono: 'text-amber-700', numero: 'text-amber-700' },
  info: { etiqueta: 'Cuando se pueda', Icono: CircleDot, caja: 'bg-gray-100', icono: 'text-sereno-dark/50', numero: 'text-sereno-dark' },
};

const ORDEN: GravedadAtencion[] = ['urgente', 'semana', 'info'];

/** «a, b y c» */
function enumerar(partes: string[]): string {
  if (partes.length <= 1) return partes.join('');
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

function FilaAsunto({ a }: { a: AsuntoAtencion }) {
  const g = GRAVEDAD[a.gravedad];
  return (
    <Link
      to={a.ruta}
      className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-sereno-blue/20 transition-colors"
    >
      <span
        className={classNames('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', g.caja)}
        title={g.etiqueta}
      >
        <g.Icono size={16} className={g.icono} aria-hidden="true" />
        <span className="sr-only">{g.etiqueta}:</span>
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium">{a.titulo}</span>
        {a.detalle && <span className="block mt-0.5 text-xs text-sereno-dark/60">{a.detalle}</span>}
      </span>
      <span className={classNames('flex-shrink-0 text-base font-bold tabular-nums', g.numero)}>{a.n}</span>
      <ChevronRight size={16} className="flex-shrink-0 text-sereno-dark/30" aria-hidden="true" />
    </Link>
  );
}

interface Props {
  atencion: Atencion | null;
  cargando: boolean;
  error: string | null;
  onReintentar: () => void;
  className?: string;
}

export default function BloquePendiente({ atencion, cargando, error, onReintentar, className }: Props) {
  const [filtro, setFiltro] = useState<'todo' | AreaAtencion>('todo');
  const asuntos = atencion?.asuntos ?? [];
  const urgentes = asuntos.filter((a) => a.gravedad === 'urgente').length;

  // Solo las áreas que tienen algo: un chip «Sistema · 0» no lleva a ninguna parte
  const areas = useMemo(
    () => AREAS_ATENCION.map((a) => ({ ...a, n: asuntos.filter((x) => x.area === a.id).length })).filter((a) => a.n > 0),
    [asuntos],
  );
  // Si al actualizar se vacía el área elegida, se vuelve a «Todo»
  const elegido = filtro !== 'todo' && areas.some((a) => a.id === filtro) ? filtro : 'todo';
  const grupos = areas
    .filter((a) => elegido === 'todo' || a.id === elegido)
    .map((a) => ({
      ...a,
      items: asuntos
        .filter((x) => x.area === a.id)
        .sort((x, y) => ORDEN.indexOf(x.gravedad) - ORDEN.indexOf(y.gravedad)),
    }));

  const resumen = !atencion
    ? ''
    : asuntos.length === 0
      ? 'Todo al día'
      : `${asuntos.length} ${asuntos.length === 1 ? 'asunto' : 'asuntos'} · ${urgentes} ${urgentes === 1 ? 'urgente' : 'urgentes'}`;

  const sinDatos = atencion?.sinDatos ?? [];
  // Si no se ha podido mirar nada, «Nada pendiente» sería mentir
  const nadaComprobado = !!atencion && asuntos.length === 0 && atencion.alDia.length === 0;

  return (
    <section aria-labelledby="t-pendiente" className={classNames('card !p-5 sm:!p-6 min-w-0', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <h2 id="t-pendiente" className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Pendiente</h2>
          {resumen && <span className="text-sm">{resumen}</span>}
        </div>
        {asuntos.length > 0 && (
          <div className="flex flex-wrap gap-3.5 text-xs text-sereno-dark/60" aria-hidden="true">
            {ORDEN.map((g) => {
              const { Icono, icono, etiqueta } = GRAVEDAD[g];
              return (
                <span key={g} className="inline-flex items-center gap-1">
                  <Icono size={13} className={icono} />
                  {etiqueta}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {!atencion && cargando && (
        <div className="mt-4 space-y-3" aria-busy="true" aria-label="Cargando lo pendiente">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <span className="w-8 h-8 rounded-lg bg-gray-100" />
              <span className="flex-1 space-y-1.5">
                <span className="block h-3 w-1/2 rounded bg-gray-100" />
                <span className="block h-2.5 w-3/4 rounded bg-gray-100" />
              </span>
            </div>
          ))}
        </div>
      )}

      {((!atencion && !cargando && error) || nadaComprobado) && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-sereno-dark/60 max-w-sm" title={error || undefined}>
            No se ha podido comprobar lo pendiente. El menú y las secciones siguen funcionando.
          </p>
          <button type="button" onClick={onReintentar} className="btn-secondary inline-flex items-center gap-2 text-sm">
            <RefreshCw size={14} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      )}

      {atencion && asuntos.length > 0 && (
        <>
          <div role="group" aria-label="Filtrar por área" className="flex flex-wrap gap-2 mt-4 mb-1">
            {[{ id: 'todo' as const, nombre: 'Todo', n: asuntos.length }, ...areas].map((a) => {
              const activo = elegido === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setFiltro(a.id)}
                  aria-pressed={activo}
                  className={classNames(
                    'min-h-[36px] px-3.5 rounded-full border text-sm font-medium transition-colors',
                    activo
                      ? 'bg-sereno-dark text-white border-sereno-dark an-active'
                      : 'bg-white text-sereno-dark border-sereno-dark/15 hover:border-sereno-dark/40',
                  )}
                >
                  {a.nombre} · {a.n}
                </button>
              );
            })}
          </div>

          {grupos.map((g) => (
            <div key={g.id} className="mt-3.5">
              <h3 className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-sereno-dark/50">{g.nombre}</h3>
              {g.items.map((a) => <FilaAsunto key={a.clave} a={a} />)}
            </div>
          ))}
        </>
      )}

      {atencion && asuntos.length === 0 && !nadaComprobado && (
        <div className="pt-10 pb-6 flex flex-col items-center gap-2.5 text-center">
          <span className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
            <Check size={24} className="text-green-700" aria-hidden="true" />
          </span>
          <p className="text-base font-semibold">Nada pendiente</p>
          <p className="text-sm text-sereno-dark/60 max-w-sm">
            Todo lo que vigila el dashboard está al día. En cuanto haya algo, aparece aquí y en el número del menú.
          </p>
        </div>
      )}

      {atencion && !nadaComprobado && (atencion.alDia.length > 0 || sinDatos.length > 0) && (
        <div className="mt-4 pt-3.5 border-t border-gray-100 space-y-1.5 text-xs text-sereno-dark/60">
          {asuntos.length > 0 && atencion.alDia.length > 0 && (
            <p className="flex items-start gap-1.5">
              <Check size={14} className="flex-shrink-0 mt-px text-green-700" aria-hidden="true" />
              <span>Al día: {enumerar(atencion.alDia)}.</span>
            </p>
          )}
          {sinDatos.length > 0 && (
            <p className="flex items-start gap-1.5">
              <AlertTriangle size={14} className="flex-shrink-0 mt-px text-amber-700" aria-hidden="true" />
              <span>No se ha podido comprobar ahora: {enumerar(sinDatos)}.</span>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
